import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { User } from '../types';
import { fetchBookings, Booking, STAGE_ORDER, isLifecycleConnected } from '../utils/lifecycleApi';
import { loadFromStorage } from '../utils/helpers';

const STAGE_ICONS = ['📋','🤝','📅','⚡','🎬','📦','🎉'];

function getStatusColor(status: 'done'|'current'|'upcoming') {
  if (status === 'done') return { bg:'#D1FAE5', border:'#059669', text:'#065F46', dot:'#059669' };
  if (status === 'current') return { bg:'#FFFBEB', border:'#B8860B', text:'#92400E', dot:'#B8860B' };
  return { bg:'#F3F4F6', border:'#D1D5DB', text:'#6B7280', dot:'#D1D5DB' };
}

export default function LifecycleMap({ user }: { user: User }) {
  const [connected] = useState(isLifecycleConnected());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Booking | null>(null);
  const [clientView, setClientView] = useState(false);

  const load = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    try { setBookings(await fetchBookings()); } catch {}
    setLoading(false);
  }, [connected]);

  useEffect(() => { load(); }, [load]);

  if (!connected) return (
    <div className="page-content">
      <div className="alert alert-error"><WifiOff size={15} /> Not connected to Lifecycle backend.</div>
    </div>
  );

  const renderMap = (b: Booking) => {
    // Defensive reads — lifecycle never crashes if planner data changes
    let links: any = {}; try { links = JSON.parse(localStorage.getItem('ss_booking_quote_links') || '{}'); } catch {}
    const linked = links[b.id];
    const quotes = loadFromStorage<any[]>('ss_quotes', []);
    const quote = linked ? quotes.find((q: any) => q.quoteNumber === linked?.quoteNumber) : null;
    const currentStageIndex = STAGE_ORDER.indexOf(b.current_stage);
    let ppData: any = {}; try { ppData = (JSON.parse(localStorage.getItem('ss_pp_tracker_v1') || '{}') || {})[b.id] || {}; } catch {}
    let costData: any[] = []; try { costData = (JSON.parse(localStorage.getItem('ss_cost_management_v1') || '{}') || {})[b.id] || []; } catch {}
    let auditData: any = {}; try { auditData = (JSON.parse(localStorage.getItem('ss_audit_checklist_v1') || '{}') || {})[b.id] || {}; } catch {}
    const ppDelivered = Object.values(ppData as any).filter((v: any) => v.status === 'Delivered').length;
    const ppTotal = Object.keys(ppData).length;
    const costPaid = (costData as any[]).filter((c: any) => c.paid).reduce((s: number, c: any) => s + c.amount, 0);
    const costTotal = (costData as any[]).reduce((s: number, c: any) => s + c.amount, 0);
    const auditDone = Object.values(auditData as any).filter(Boolean).length;

    // Build the journey nodes
    const nodes: { id: string; label: string; sublabel: string; status: 'done'|'current'|'upcoming'; ref?: string; icon: string; detail?: string }[] = [];

    // Node 1: Enquiry
    if (b.service_type) {
      nodes.push({ id:'enquiry', label:'Enquiry Created', sublabel: b.service_type, status:'done', icon:'📞',
        ref: linked?.enquiryRef || '', detail: `Customer: ${b.customer_name}` });
    }

    // Node 2: Quote
    if (quote) {
      nodes.push({ id:'quote', label:'Quotation Shared', sublabel: quote.quoteNumber, status:'done', icon:'📄',
        ref: quote.quoteNumber, detail: `₹${(quote.quotedAmount||0).toLocaleString('en-IN')} · ${quote.packageItems?.[0]?.productName||''}` });
    }

    // Node 3: Invoice + Advance
    if (quote && (quote as any).documentType === 'invoice' && (quote as any).advanceReceived) {
      nodes.push({ id:'invoice', label:'Invoice Raised', sublabel:'Advance Received', status:'done', icon:'💰',
        ref: (quote as any).invoiceNumber || b.invoice_ref_system || '', detail: 'Payment confirmed' });
    } else if (quote && (quote as any).documentType === 'invoice') {
      nodes.push({ id:'invoice', label:'Invoice Sent', sublabel:'Awaiting advance', status:'current', icon:'💰',
        ref: (quote as any).invoiceNumber || b.invoice_ref_system || '', detail: 'Invoice sent to customer — confirm once advance is received' });
    } else if (quote) {
      nodes.push({ id:'invoice', label:'Invoice Pending', sublabel:'Awaiting confirmation', status:'current', icon:'💰', detail: 'Convert quotation to invoice, then confirm once advance is received' });
    }

    // Node 4: Booking Confirmed
    nodes.push({ id:'booking', label:'Booking Confirmed', sublabel: b.csr_number, status:'done', icon:'✅',
      ref: b.csr_number, detail: `CSR: ${b.csr_number} · ${b.service_type}` });

    // Nodes 5-11: 7 stages
    STAGE_ORDER.forEach((stage, i) => {
      const status: 'done'|'current'|'upcoming' = i < currentStageIndex ? 'done' : i === currentStageIndex ? 'current' : 'upcoming';
      nodes.push({ id: `stage-${i}`, label: stage, sublabel: status === 'done' ? '✓ Completed' : status === 'current' ? '● In Progress' : 'Upcoming',
        status, icon: STAGE_ICONS[i], detail: stage });
    });

    // Post-production status
    if (ppTotal > 0) {
      nodes.push({ id:'pp', label:'Post-Production', sublabel: `${ppDelivered}/${ppTotal} delivered`,
        status: ppDelivered === ppTotal ? 'done' : ppDelivered > 0 ? 'current' : 'upcoming', icon:'🎬',
        detail: `${ppDelivered} of ${ppTotal} deliverables completed` });
    }

    // Financials
    if (costTotal > 0) {
      nodes.push({ id:'cost', label:'Payments', sublabel: `₹${costPaid.toLocaleString('en-IN')} / ₹${costTotal.toLocaleString('en-IN')}`,
        status: costPaid >= costTotal ? 'done' : costPaid > 0 ? 'current' : 'upcoming', icon:'💳',
        detail: `Paid: ₹${costPaid.toLocaleString('en-IN')} · Outstanding: ₹${(costTotal-costPaid).toLocaleString('en-IN')}` });
    }

    // Client view — simplified milestones only
    const clientNodes = nodes.filter(n => ['enquiry','quote','invoice','booking',`stage-${STAGE_ORDER.indexOf('Event Execution')}`,`stage-${STAGE_ORDER.indexOf('Final Delivery')}`,'stage-6'].includes(n.id));
    const displayNodes = clientView ? clientNodes : nodes;

    return (
      <div>
        {/* Client/Team toggle */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:16 }}>{b.customer_name} — {b.csr_number}</div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{b.service_type} · Current: <strong>{b.current_stage}</strong></div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>View:</span>
            <button onClick={() => setClientView(false)} style={{ padding:'5px 14px', borderRadius:100, fontSize:12, cursor:'pointer', fontFamily:'inherit', fontWeight:!clientView?700:400, background:!clientView?'#1F2937':'transparent', color:!clientView?'#fff':'var(--text-muted)', border:`1px solid ${!clientView?'#1F2937':'var(--border)'}` }}>Team</button>
            <button onClick={() => setClientView(true)} style={{ padding:'5px 14px', borderRadius:100, fontSize:12, cursor:'pointer', fontFamily:'inherit', fontWeight:clientView?700:400, background:clientView?'var(--gold)':'transparent', color:clientView?'#fff':'var(--text-muted)', border:`1px solid ${clientView?'var(--gold)':'var(--border)'}` }}>Client</button>
          </div>
        </div>

        {clientView && (
          <div style={{ padding:'10px 14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:20, fontSize:12, color:'#92400E' }}>
            👤 Client View — simplified journey. Share this view when client asks "where are we?"
          </div>
        )}

        {/* Visual flow — vertical on mobile, wraps */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:0, alignItems:'flex-start' }}>
          {displayNodes.map((node, idx) => {
            const c = getStatusColor(node.status);
            return (
              <div key={node.id} style={{ display:'flex', alignItems:'center' }}>
                {/* Node */}
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, minWidth:120, maxWidth:140 }}>
                  {/* Circle */}
                  <div style={{ width:48, height:48, borderRadius:'50%', background:c.bg, border:`2px solid ${c.border}`,
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, position:'relative' }}>
                    {node.status === 'done' && <div style={{ position:'absolute', top:-4, right:-4, width:16, height:16, borderRadius:'50%', background:'#059669', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:10, fontWeight:700 }}>✓</span></div>}
                    {node.status === 'current' && <div style={{ position:'absolute', top:-4, right:-4, width:16, height:16, borderRadius:'50%', background:'#B8860B', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:8, fontWeight:700 }}>●</span></div>}
                    {node.icon}
                  </div>
                  {/* Label */}
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:11, fontWeight:700, color:c.text, lineHeight:1.3 }}>{node.label}</div>
                    <div style={{ fontSize:10, color:node.status==='done'?'#059669':node.status==='current'?'#B8860B':'#9CA3AF', marginTop:2 }}>{node.sublabel}</div>
                    {!clientView && node.ref && <div style={{ fontSize:9, color:'#9CA3AF', marginTop:1, fontFamily:'monospace' }}>{node.ref}</div>}
                    {!clientView && node.detail && node.status==='current' && <div style={{ fontSize:9, color:'#B8860B', marginTop:2, maxWidth:110, textAlign:'center', lineHeight:1.3 }}>{node.detail}</div>}
                  </div>
                </div>
                {/* Connector arrow */}
                {idx < displayNodes.length - 1 && (
                  <div style={{ width:32, height:2, background: displayNodes[idx+1].status==='upcoming'?'#E5E7EB':'#059669', margin:'0 2px', marginBottom:28, position:'relative', flexShrink:0 }}>
                    <div style={{ position:'absolute', right:-4, top:-4, width:0, height:0, borderTop:'5px solid transparent', borderBottom:'5px solid transparent', borderLeft:`6px solid ${displayNodes[idx+1].status==='upcoming'?'#E5E7EB':'#059669'}` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display:'flex', gap:16, marginTop:24, flexWrap:'wrap' }}>
          {[{color:'#059669',label:'Completed'},{color:'#B8860B',label:'In Progress'},{color:'#D1D5DB',label:'Upcoming'}].map(l => (
            <div key={l.label} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'#6B7280' }}>
              <div style={{ width:10, height:10, borderRadius:'50%', background:l.color }} /> {l.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>Lifecycle Map</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:13 }}>Dynamic journey map — auto-generated per booking. Team view or Client view.</p>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={load}><RefreshCw size={15} /></button>
      </div>

      {/* Booking selector */}
      <div style={{ marginBottom:24 }}>
        <label style={{ fontSize:12, marginBottom:6, display:'block' }}>Select Booking / Client</label>
        <select className="input" style={{ maxWidth:400 }} value={selected?.id || ''} onChange={e => {
          const b = bookings.find(x => x.id === Number(e.target.value));
          setSelected(b || null);
        }}>
          <option value="">— Choose a booking —</option>
          {bookings.map(b => (
            <option key={b.id} value={b.id}>{b.customer_name} · {b.csr_number} · {b.current_stage}</option>
          ))}
        </select>
      </div>

      {loading && <div style={{ color:'var(--text-muted)', fontSize:13 }}>Loading bookings…</div>}
      {!loading && !selected && (
        <div style={{ textAlign:'center', padding:60, color:'var(--text-muted)', background:'var(--bg-subtle)', borderRadius:12 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🗺️</div>
          <div style={{ fontWeight:600, marginBottom:4 }}>Select a booking above</div>
          <div style={{ fontSize:13 }}>The complete journey map will appear here — from first enquiry to final delivery.</div>
        </div>
      )}
      {selected && (
        <div className="card">
          {renderMap(selected)}
        </div>
      )}
    </div>
  );
}
